'use client';

import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useInvite } from '@/lib/api/referrals/hooks';
import { useIsMobile } from '@/lib/hooks/useIsMobile';
import { useShareImageBlob } from '@/lib/hooks/useShareImageBlob';
import { Dialog, DialogClose, DialogContent, DialogTitle } from '@/lib/ui-kit/dialog';
import { Icon } from '@/lib/ui-kit/icons';
import { Button } from '@/lib/ui-kit-v2/button';
import { canShareNatively, shareImageNatively } from '@/lib/utils/nativeShare';
import { preloadImages } from '@/lib/utils/preloadImage';
import { SharePositionCard, type SharePositionCardProps } from './SharePositionCard';

export interface DialogSharePositionProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  position: {
    eventTitle: string;
    eventImage: string;
    outcome: 'Yes' | 'No';
    outcomeName?: string;
    side?: 'Buy' | 'Sell';
    entryPrice: number;
    exitPrice?: number;
    myBet: number;
    possibleWin: number;
    pnl?: number;
    pnlPercent?: number;
    isResolved?: boolean;
    isWon?: boolean;
    isSettlement?: boolean;
  };
}

export function DialogSharePosition({ open, onOpenChange, position }: DialogSharePositionProps) {
  const t = useTranslations('sharePosition');
  const tShare = useTranslations('share');
  const tCommon = useTranslations('common');
  const cardRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();
  const [isProcessing, setIsProcessing] = useState(false);
  const [showSocialPanel, setShowSocialPanel] = useState(false);

  const { data: inviteData, isPending: isInviteLoading } = useInvite();

  // Reset social panel when dialog closes
  useEffect(() => {
    if (!open) {
      setShowSocialPanel(false);
      return;
    }

    preloadImages(
      [
        '/share-card-bg.png',
        '/share-card-logo.svg',
        '/share-card-divider.svg',
        position.eventImage,
      ],
      'high',
    );
  }, [open, position.eventImage]);

  const referralCode = inviteData?.ic ?? '';
  const referralLink = inviteData?.il ?? '';

  const getImageBlob = useShareImageBlob(cardRef, {
    scale: 2,
    warm: open && !isInviteLoading,
  });

  const handleCopyImage = useCallback(async () => {
    if (isProcessing) return;

    setIsProcessing(true);
    try {
      const blob = await getImageBlob();

      if (!blob) {
        toast.error(t('copyFailed'));
        setIsProcessing(false);
        return;
      }

      try {
        await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
        toast.success(t('imageCopied'));
      } catch {
        // Fallback: download the image
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'outpoll-position.png';
        a.click();
        URL.revokeObjectURL(url);
        toast.success(t('imageDownloaded'));
      }
    } catch (error) {
      console.error('Failed to generate image:', error);
      toast.error(t('copyFailed'));
    } finally {
      setIsProcessing(false);
    }
  }, [isProcessing, getImageBlob, t]);

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  const referralShareUrl = referralLink || shareUrl;
  const inviteText = t('inviteMessage');

  const handleShare = useCallback(async () => {
    if (isProcessing) return;

    if (!isMobile || !canShareNatively()) {
      setShowSocialPanel((prev) => !prev);
      return;
    }

    setIsProcessing(true);
    try {
      const blob = await getImageBlob();
      const result = await shareImageNatively(blob, {
        fileName: 'outpoll-position.png',
        text: inviteText,
        url: referralShareUrl,
      });
      if (result === 'failed') setShowSocialPanel(true);
    } finally {
      setIsProcessing(false);
    }
  }, [isProcessing, isMobile, getImageBlob, inviteText, referralShareUrl]);

  const encodedUrl = encodeURIComponent(referralShareUrl);
  const encodedText = encodeURIComponent(inviteText);
  const socialLinks = [
    {
      icon: 'Facebook',
      label: tShare('shareOnFacebook'),
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
    },
    {
      icon: 'WhatsApp',
      label: tShare('shareOnWhatsApp'),
      href: `https://wa.me/?text=${encodeURIComponent(`${inviteText} ${referralShareUrl}`)}`,
    },
    {
      icon: 'Telegram',
      label: tShare('shareOnTelegram'),
      href: `https://t.me/share/url?url=${encodedUrl}&text=${encodedText}`,
    },
    {
      icon: 'Twitter',
      label: tShare('shareOnX'),
      href: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedText}`,
    },
  ] as const;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(`${inviteText} ${referralShareUrl}`);
      toast.success(tShare('linkCopied'));
    } catch {
      toast.error(tShare('failedToCopy'));
    }
  };

  const cardProps: SharePositionCardProps = {
    eventTitle: position.eventTitle,
    eventImage: position.eventImage,
    outcome: position.outcome,
    outcomeName: position.outcomeName,
    side: position.side,
    entryPrice: position.entryPrice,
    exitPrice: position.exitPrice,
    myBet: position.myBet,
    possibleWin: position.possibleWin,
    pnl: position.pnl,
    pnlPercent: position.pnlPercent,
    isResolved: position.isResolved,
    isWon: position.isWon,
    isSettlement: position.isSettlement,
    referralCode,
    referralLink,
    isLoading: isInviteLoading,
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-[400px] gap-6 rounded-2xl border-v2-border p-6"
        showCloseButton={false}
      >
        <div className="relative flex items-center justify-center">
          <DialogTitle className="pr-0 text-center text-2xl font-semibold leading-8">
            {t('title')}
          </DialogTitle>
          <DialogClose className="absolute right-0 opacity-70 transition-opacity hover:opacity-100">
            <Icon name="Close" size={24} />
            <span className="sr-only">{tCommon('close')}</span>
          </DialogClose>
        </div>

        {/* Card preview */}
        <div className="flex justify-center">
          <SharePositionCard ref={cardRef} {...cardProps} />
        </div>

        {/* Action buttons */}
        <div className="flex gap-3">
          <Button
            appearance="outline"
            size="M"
            className="flex-1"
            onClick={handleCopyImage}
            disabled={isProcessing}
            leftIcon={<Icon name="Copy" size={20} />}
          >
            {t('copyImage')}
          </Button>
          <Button
            appearance="accent"
            size="M"
            className="flex-1"
            onClick={handleShare}
            disabled={isProcessing}
            leftIcon={<Icon name="Share2" size={20} />}
          >
            {t('share')}
          </Button>
        </div>

        {/* Social share panel — animates via DialogContent's [&>*]:animate-in */}
        {showSocialPanel && (
          <div className="flex items-center justify-center gap-2 pt-1">
            {socialLinks.map(({ icon, label, href }) => (
              <Button
                key={icon}
                appearance="icon"
                size="M"
                onClick={() =>
                  window.open(href, '_blank', 'width=600,height=400,noopener,noreferrer')
                }
                aria-label={label}
                title={label}
              >
                <Icon name={icon} size={20} />
              </Button>
            ))}
            <Button
              appearance="icon"
              size="M"
              onClick={handleCopyLink}
              aria-label={tShare('copyLink')}
              title={tShare('copyLink')}
            >
              <Icon name="Link" size={20} />
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
